import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";
import type { SponsorTier } from "@wondermaps/db";

interface MapSponsorItem {
  sponsorId: string;
  sponsorName: string;
  tier: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

const TIER_ORDER: SponsorTier[] = ["gold", "silver", "bronze"];

export function MapSponsorROI({ data }: { data: MapSponsorItem[] }) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Map Sponsor Logo ROI</CardTitle>
        <CardDescription>
          Measures impressions, clicks, and click-through rate for map-level
          sponsor logos. Helps justify sponsorship pricing and demonstrates
          brand visibility to current and prospective sponsors.
        </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No sponsor impression data in this period.</p>
        </CardContent>
      </Card>
    );
  }

  const grouped: { tier: string; items: MapSponsorItem[] }[] = TIER_ORDER.map((tier) => ({
    tier,
    items: data.filter((d) => d.tier === tier),
  })).filter((g) => g.items.length > 0);

  // Include any sponsors not in standard tiers
  const unmatched = data.filter((d) => !(TIER_ORDER as readonly string[]).includes(d.tier));
  if (unmatched.length > 0) {
    grouped.push({ tier: "other", items: unmatched });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Map Sponsor Logo ROI</CardTitle>
        <CardDescription>
          Measures impressions, clicks, and click-through rate for map-level
          sponsor logos. Helps justify sponsorship pricing and demonstrates
          brand visibility to current and prospective sponsors.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {grouped.map(({ tier, items }) => (
          <div key={tier}>
            <Badge variant="outline" className="mb-2 capitalize">
              {tier}
            </Badge>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sponsor</TableHead>
                  <TableHead className="text-right">Impressions</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.sponsorId}>
                    <TableCell className="font-medium">{item.sponsorName}</TableCell>
                    <TableCell className="text-right">{item.impressions.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.clicks.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.ctr}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
